import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { marketplaceApi } from "../api/client";
import MarketplaceHeader from "../components/MarketplaceHeader";
import ProductCard from "../components/ProductCard";
import TransporterServiceCard from "../components/TransporterServiceCard";
import Footer from "../components/Footer";

function isSafeInternalHref(href) {
  return typeof href === "string" && href.startsWith("/") && !href.startsWith("//");
}

function renderRichAnswer(text) {
  const source = String(text || "").trim();
  if (!source) return null;

  const paragraphs = source.split(/\n{2,}/).filter(Boolean);
  const tokenRegex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;

  return paragraphs.map((paragraph, paragraphIndex) => {
    const pieces = [];
    let lastIndex = 0;
    let match;
    tokenRegex.lastIndex = 0;

    while ((match = tokenRegex.exec(paragraph)) !== null) {
      if (match.index > lastIndex) {
        pieces.push(paragraph.slice(lastIndex, match.index));
      }

      if (match[2] && match[3]) {
        const label = match[2];
        const href = match[3];
        if (isSafeInternalHref(href)) {
          pieces.push(
            <Link key={`link-${paragraphIndex}-${match.index}`} to={href} className="public-ai-answer-link">
              {label}
            </Link>
          );
        } else if (/^https?:\/\//i.test(href)) {
          pieces.push(
            <a key={`link-${paragraphIndex}-${match.index}`} href={href} target="_blank" rel="noreferrer" className="public-ai-answer-link">
              {label}
            </a>
          );
        } else {
          pieces.push(label);
        }
      } else if (match[4]) {
        pieces.push(<strong key={`bold-${paragraphIndex}-${match.index}`}>{match[4]}</strong>);
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < paragraph.length) {
      pieces.push(paragraph.slice(lastIndex));
    }

    return <p key={`answer-paragraph-${paragraphIndex}`}>{pieces}</p>;
  });
}

export default function PublicAI() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuestion = searchParams.get("q") || "";
  const [question, setQuestion] = useState(initialQuestion);
  const [answer, setAnswer] = useState(null);
  const [results, setResults] = useState({ products: [], sellers: [], transporter_services: [] });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const matchedProduct = results.products?.[0] || null;
  const matchedSeller = results.sellers?.[0] || null;
  const matchedTransporter = results.transporter_services?.[0] || null;

  const title = useMemo(() => initialQuestion ? `TAZY AI: ${initialQuestion}` : "Ask TAZY AI", [initialQuestion]);

  async function askAi(rawQuestion = question) {
    const clean = String(rawQuestion || "").trim();
    if (!clean) return;
    setLoading(true);
    setError("");
    setAnswer(null);
    try {
      const res = await marketplaceApi.publicAiAssist({ message: clean, context_type: "PUBLIC_MARKETPLACE" });
      setAnswer(res.data);
      setResults(res.data?.results || { products: [], sellers: [], transporter_services: [] });
      setCounts(res.data?.counts || {});
      const params = new URLSearchParams();
      params.set("q", clean);
      navigate(`/ai?${params.toString()}`, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "TAZY AI could not answer right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (initialQuestion) askAi(initialQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit(event) {
    event.preventDefault();
    askAi(question);
  }

  return (
    <div className="page-shell marketplace-page public-ai-page" id="top">
      <MarketplaceHeader />
      <main>
        <section className="public-ai-hero">
          <div className="container public-ai-hero__inner">
            <div>
              <span className="eyebrow">TAZY AI MARKETPLACE ASSISTANT</span>
              <h1>{title}</h1>
              <p>Ask about products, verified sellers, sourcing ideas and transporter services directly from the marketplace.</p>
            </div>
            <Link className="public-ai-hero__link" to="/">Back to marketplace</Link>
          </div>
        </section>

        <section className="container public-ai-search-card">
          <form className="public-ai-search-form" onSubmit={submit}>
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Ask TAZY AI what you want to source, compare or find..."
            />
            <button type="submit" disabled={loading || !question.trim()}>{loading ? "Thinking..." : "Ask TAZY AI"}</button>
          </form>
          <div className="public-ai-helper">Examples: best verified phone sellers, affordable transporters to Victoria Falls, groceries suppliers, hardware items in stock.</div>
        </section>

        <section className="container public-ai-answer-grid">
          <article className="public-ai-answer-card">
            <div className="section-heading compact-heading">
              <span>Answer</span>
              <h2>Marketplace guidance</h2>
            </div>
            {loading ? (
              <div className="public-ai-loading">TAZY AI is checking products, sellers and transporters...</div>
            ) : error ? (
              <div className="public-ai-error">{error}</div>
            ) : answer ? (
              <>
                <div className="public-ai-answer-text public-ai-answer-rich">{renderRichAnswer(answer.answer)}</div>
                <div className="public-ai-counts">
                  <span>{counts.products || 0} products</span>
                  <span>{counts.sellers || 0} sellers</span>
                  <span>{counts.transporters || 0} transporters</span>
                  <span>{counts.categories || 0} categories</span>
                </div>
                {(matchedProduct || matchedSeller || matchedTransporter) ? (
                  <div className="public-ai-answer-actions">
                    {matchedProduct ? (
                      <Link to={`/products/${matchedProduct.slug || matchedProduct.code || matchedProduct.id}`}>View matched product</Link>
                    ) : null}
                    {matchedSeller ? <Link to="/sellers">View seller matches</Link> : null}
                    {matchedTransporter ? <Link to="/transporters">View transporter matches</Link> : null}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="public-ai-empty">Enter a question above and TAZY AI will answer using marketplace information.</div>
            )}
          </article>

          <aside className="public-ai-side-card">
            <span>Buyer action</span>
            <h3>Ready to act?</h3>
            <p>Open a product to place an order or send a protected enquiry through TAZY CART.</p>
            <div className="public-ai-side-actions">
              <Link to="/search">Search products</Link>
              <Link to="/sellers">Verified sellers</Link>
              <Link to="/transporters">Transporters</Link>
            </div>
          </aside>
        </section>

        {(results.products?.length || results.sellers?.length || results.transporter_services?.length) ? (
          <section className="container public-ai-results-section">
            {results.products?.length ? (
              <div className="market-block">
                <div className="section-heading compact-heading"><span>Matches</span><h2>Products TAZY AI considered</h2></div>
                <div className="product-grid compact-product-grid">
                  {results.products.map((item) => <ProductCard key={item.id || item.slug || item.code} item={item} />)}
                </div>
              </div>
            ) : null}

            {results.sellers?.length ? (
              <div className="public-ai-mini-section">
                <div className="section-heading compact-heading"><span>Verified</span><h2>Seller matches</h2></div>
                <div className="public-ai-seller-grid">
                  {results.sellers.map((seller) => (
                    <article key={seller.id} className="seller-mini-card">
                      <strong>{seller.store_name}</strong>
                      <span>{seller.city || seller.province || "Marketplace seller"}</span>
                      <p>{seller.description || seller.category || "Verified seller on TAZY CART."}</p>
                      <Link to="/sellers">View seller on TAZY CART</Link>
                    </article>
                  ))}
                </div>
              </div>
            ) : null}

            {results.transporter_services?.length ? (
              <div className="public-ai-mini-section">
                <div className="section-heading compact-heading"><span>Logistics</span><h2>Transporter service matches</h2></div>
                <div className="transporter-service-grid compact-transporter-grid">
                  {results.transporter_services.map((service) => <TransporterServiceCard key={service.id} service={service} />)}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
